import RAPIER from '@dimforge/rapier3d-compat';
import { Color, Object3D, Vector3 } from 'three';
import type { LightDirector, LightSource } from '../render/light-director';
import { LIGHTS } from '../render/palette';
import { BALANCE } from '../sim/balance';
import { GuardBrain, type GuardMode, type Sighting } from '../sim/guard-ai';
import type { Round } from '../sim/round';
import type { Contact } from '../sim/security';
import type { PlayerId, Vec3 } from '../sim/types';
import { GuardFigure } from '../world/guard-figure';
import type { PatrolRoute, ZoneDef } from '../world/plan';
import { zoneAt } from '../world/plan';
import type { NavGrid } from './navgrid';
import type { MallPhysics } from './physics';

/**
 * GUARDS, AS BODIES.
 *
 * `src/sim/guard-ai.ts` decides what a guard wants. This makes it happen: a
 * kinematic capsule that walks the same collide-and-slide controller the player
 * does, a path over the nav grid, a figure to look at, and a pair of eyes that
 * turn "is that thief visible" into a raycast.
 *
 * THE GUARD IS BUILT LIKE THE PLAYER ON PURPOSE. Same capsule, same autostep,
 * same slope limits, same fixed step. Anything a guard can walk over, a player
 * can, and — more to the point — the reverse: a thief cannot escape by taking a
 * kerb the guard's controller refuses. Two different movement systems in a chase
 * game means one of them is wrong in a way you only discover when a player finds
 * the ledge that guards will not step onto.
 *
 * PATHS ARE RATIONED. A* over ninety thousand nodes costs a millisecond or two
 * when the goal is reachable and rather more when it is a fresh island; three
 * guards repathing every frame during a chase is the sort of thing that quietly
 * eats a third of the frame budget. So the corps grants exactly ONE repath per
 * tick, round-robin, and a guard between grants keeps walking the path it has.
 * Since a path is a list of corners and the target moves smoothly, the cost of
 * being one tick stale is a few centimetres.
 *
 * SIGHT IS ONE RAY PER THIEF PER GUARD, and only after the cheap tests. Range
 * first, then the cone, then the ray — so a thief on the far side of the mall
 * costs a subtraction and a comparison.
 */

const M = BALANCE.move;
const G = BALANCE.guard;

const SKIN = 0.02;
/** Guards are a shade shorter than the player. Nothing rides on it; it reads. */
const HEIGHT = 1.82;
const EYE = HEIGHT * 0.88;

/** How fast the body turns, radians per second, ambling and pursuing. */
const TURN_SLOW = 2.4;
const TURN_FAST = 5.6;
/** The head leads the body by at most this, in radians. */
const NECK = 1.0;
/** Peak of the idle head sweep while standing and scanning, in radians. */
const SCAN_ARC = 0.85;
const SCAN_RATE = 0.85;

/** Within this of the current waypoint, take the next one. */
const WAYPOINT_M = 0.55;
/** Within this of the final goal, the brain is told it has arrived. */
const ARRIVED_M = 1.1;
/** Seconds before a path is refreshed even if nothing has changed. */
const REPATH_SEC = 2.4;
/** The goal moving further than this invalidates the path immediately. */
const GOAL_DRIFT_M = 1.4;
/** Wanting to move but covering less than this per second means stuck. */
const STUCK_MPS = 0.35;
const STUCK_SEC = 0.9;

/**
 * A thief as a guard's eyes see them.
 *
 * Deliberately not `PlayerState`. Two of the things that decide whether a guard
 * notices you — how fast you are going and whether you are crouched — live in
 * the movement controller, not in the round state, and the sim layer is not
 * allowed to reach into the controller. So the session assembles this each tick
 * from both halves, and the guards only ever see the assembled view.
 */
export interface ThiefSense {
  id: PlayerId;
  pos: Vec3;
  speed: number;
  crouching: boolean;
  /** Caught or escaped thieves are invisible; there is nothing left to chase. */
  active: boolean;
  /** How brightly the shop fittings are lighting them, 0..1. */
  exposure: number;
}

export interface GuardCorpsOptions {
  physics: MallPhysics;
  nav: NavGrid;
  round: Round;
  zones: ZoneDef[];
  routes: PatrolRoute[];
  /** Fallback spawns, used when a route is empty. */
  spawns: Vec3[];
  /** Guard figures are parented here, outside the static batch. */
  stage: Object3D;
  lights: LightDirector;
}

/** One thing a guard said, and where they were standing when they said it. */
export interface RadioLine {
  id: PlayerId;
  line: string;
  at: Vec3;
}

/** What the session needs back each tick. */
export interface CorpsFrame {
  /** Highest awareness any guard has of the local thief, 0..1. */
  detection: number;
  /** Radio traffic this tick, in the order it was said. */
  chatter: RadioLine[];
  /** True on the tick a guard actually laid hands on someone. */
  caught: PlayerId | null;
}

export class GuardCorps {
  readonly guards: Guard[] = [];

  /**
   * Turns the night shift off.
   *
   * Purely for the QA harness. A scripted bot walking a fixed line to prove the
   * jewellery vitrine is reachable is not playing stealth, and it should not be
   * caught doing it — the map tests and the guard tests want the building in
   * different states, and this is how they get it. Never touched by the game.
   */
  enabled = true;

  private readonly opts: GuardCorpsOptions;
  /** Swapped out on restart; the guards themselves outlive the round. */
  private round: Round;
  private repathTurn = 0;
  private readonly frame: CorpsFrame = { detection: 0, chatter: [], caught: null };

  constructor(opts: GuardCorpsOptions) {
    this.opts = opts;
    this.round = opts.round;
    opts.routes.forEach((route, i) => {
      const start = route.at[0] ?? opts.spawns[i] ?? { x: 0, y: 0, z: 0 };
      this.guards.push(new Guard(`guard${i + 1}`, route, start, opts, i));
    });
    // Built at node 0, then immediately dealt their real posts. Going through
    // `reset` rather than duplicating the placement here is what stops the first
    // night of a session from being the one night that is never randomised.
    this.reset(opts.round);
  }

  /**
   * One fixed step of every guard.
   *
   * Called from the session's physics loop rather than its frame loop, because a
   * guard is a character controller and a character controller integrated on a
   * variable step is a guard who climbs different slopes at different frame
   * rates. See the note in `Session.update`.
   */
  update(dt: number, thieves: ThiefSense[], contacts: Contact[], watching: PlayerId): CorpsFrame {
    const f = this.frame;
    f.detection = 0;
    f.chatter.length = 0;
    f.caught = null;
    if (!this.enabled) return f;

    // One repath per tick, handed out in rotation. A guard with no path at all
    // jumps the queue — standing still is much worse than being a tick stale.
    let grant: Guard | null = null;
    for (let i = 0; i < this.guards.length && !grant; i++) {
      const g = this.guards[(this.repathTurn + i) % this.guards.length];
      if (g.wantsRepath && !g.hasPath) grant = g;
    }
    for (let i = 0; i < this.guards.length && !grant; i++) {
      const g = this.guards[(this.repathTurn + i) % this.guards.length];
      if (g.wantsRepath) grant = g;
    }
    if (grant) this.repathTurn = (this.guards.indexOf(grant) + 1) % this.guards.length;

    for (const guard of this.guards) {
      const state = this.round.players.get(guard.id);
      // The round decides a guard goes down (a thief shoved them); the brain
      // decides when they get up. Push the round's verdict in before thinking so
      // the shove lands on the same tick it happened.
      if (state?.status === 'downed' && !guard.brain.isDown) guard.brain.knockDown();

      const say = guard.step(dt, thieves, contacts, guard === grant);
      // The line carries who said it and where they were standing, because the
      // toast is only half of what a radio call is for — the other half is the
      // sound of it coming from two rooms to your left.
      if (say) f.chatter.push({ id: guard.id, line: `${guard.callsign} · ${say}`, at: guard.pos });

      if (state) {
        state.pos.x = guard.pos.x;
        state.pos.y = guard.pos.y;
        state.pos.z = guard.pos.z;
        if (!guard.brain.isDown && state.status === 'downed') this.round.reviveGuard(guard.id);
      }

      if (guard.grabbing) {
        const r = this.round.capture(guard.id, guard.grabbing);
        if (r.ok) f.caught = guard.grabbing;
      }

      const a = guard.brain.awarenessOf(watching);
      if (a > f.detection) f.detection = a;
    }
    return f;
  }

  /** Keeps figures and torch lights in step with the render frame, not the tick. */
  render(dt: number): void {
    for (const guard of this.guards) guard.render(dt);
  }

  /**
   * Put the night shift where the host says it is.
   *
   * On a client no brain runs and no path is planned: three guards are three
   * transforms arriving twenty times a second, and everything else about them —
   * the walk cycle, the torch, the head lead — is derived locally from those
   * transforms. That is deliberate. Running the AI on both machines and hoping it
   * agrees is the classic way to get a guard who is chasing you on your screen
   * and standing still on your friend's, and there is nothing in a guard's
   * behaviour worth the determinism budget it would cost to keep in lockstep.
   */
  applyNet(i: number, x: number, y: number, z: number, yaw: number, alert: number, mode: GuardMode): void {
    this.guards[i]?.applyNet(x, y, z, yaw, alert, mode);
  }

  /**
   * A thief shoves a guard from behind.
   *
   * The only counterplay a thief has, and it is deliberately a stealth move
   * rather than a get-out-of-jail card: it only lands from the rear arc, so a
   * guard who has already turned round and seen you cannot be shoved over. That
   * makes it a way to clear a patrol off a corridor you need, not a way to win a
   * chase you have already lost.
   */
  shove(from: Vec3, byId: PlayerId): { ok: boolean; message: string } {
    let best: Guard | null = null;
    let bestD = G.grabM + 0.4;
    for (const guard of this.guards) {
      if (guard.brain.isDown) continue;
      const d = Math.hypot(guard.pos.x - from.x, guard.pos.z - from.z);
      if (d > bestD || Math.abs(guard.pos.y - from.y) > 1.4) continue;
      best = guard;
      bestD = d;
    }
    if (!best) return { ok: false, message: 'Nobody to shove' };
    // Behind means behind: the guard's own facing, not the camera's.
    const toThief = Math.atan2(-(from.x - best.pos.x), -(from.z - best.pos.z));
    const rear = Math.abs(wrapPi(toThief - best.yaw));
    if (rear < Math.PI * 0.55) return { ok: false, message: 'They saw you coming' };
    this.round.downGuard(best.id, byId);
    best.brain.knockDown();
    return { ok: true, message: `${best.callsign} down` };
  }

  /**
   * Re-enrol in a fresh round and go back to the start of the beat.
   *
   * The corps deliberately survives a restart — three capsules, three figures
   * and three brains are worth keeping — so the only things that have to move
   * are the ones the last round changed: who the round thinks the guards are,
   * where they are standing, and what they think they saw.
   */
  reset(round: Round): void {
    this.round = round;
    this.repathTurn = 0;
    // One stream for the whole shift, drawn in guard order, so adding a fourth
    // route cannot change where the first three start.
    const rng = round.rngFor('patrol');
    this.guards.forEach((guard, i) => {
      const route = this.opts.routes[i];
      const nodes = route?.at.length ?? 0;
      // Anywhere on the loop, either way round. The building is the same building
      // every night; where the people in it happen to be is not, and that alone is
      // the difference between casing a mall and replaying a solved puzzle.
      const idx = nodes ? rng.int(nodes) : 0;
      const dir: 1 | -1 = rng.next() < 0.5 ? -1 : 1;
      const start = route?.at[idx] ?? this.opts.spawns[i] ?? { x: 0, y: 0, z: 0 };
      guard.reset(start, idx, dir);
      round.addPlayer(guard.id, route?.name ?? guard.id, 'guard', start);
    });
  }

  /** Debug/QA view. Plain data, no three.js. */
  get snapshot() {
    return this.guards.map((g) => g.snapshot);
  }

  dispose(): void {
    for (const g of this.guards) g.dispose();
  }
}

// ---------------------------------------------------------------------------

export class Guard {
  readonly brain: GuardBrain;
  readonly figure: GuardFigure;
  readonly pos = new Vector3();
  readonly callsign: string;
  /** Set on the tick the brain asks for a tackle; the corps clears it. */
  grabbing: PlayerId | null = null;
  /** True when this guard would like a fresh path. The corps rations these. */
  wantsRepath = false;
  yaw = 0;

  private readonly controller: RAPIER.KinematicCharacterController;
  private readonly collider: RAPIER.Collider;
  private readonly body: RAPIER.RigidBody;
  private readonly opts: GuardCorpsOptions;
  private readonly vel = new Vector3();
  private readonly eye = { x: 0, y: 0, z: 0 };
  private readonly torch: LightSource;
  private readonly dropTorch: () => void;

  private path: Vec3[] = [];
  private leg = 0;
  private pathGoal: Vec3 | null = null;
  /**
   * The last place the brain actually asked to be, which is NOT the same as the
   * place the pathfinder was last aimed at.
   *
   * The brain says "goal: null" to mean "stay here", and it says it precisely
   * while standing at a waypoint waiting out its dwell. If arrival is measured
   * against the pathfinder's goal, that null wipes the goal, arrival reads as
   * true forever, and the guard spends the whole round dwelling at its spawn.
   * It has to be measured against the last real goal, which outlives the nulls.
   */
  private lastGoal: Vec3 | null = null;
  private repathIn = 0;
  private stuckFor = 0;
  private lastX = 0;
  private lastZ = 0;
  private unreachable = false;
  private headYaw = 0;
  private headPitch = 0;
  private scanPhase = 0;
  private downBlend = 0;
  /** Metres per second last tick. Read by the audio layer to time the gait. */
  speed = 0;
  private sightings: Sighting[] = [];

  constructor(
    readonly id: PlayerId,
    route: PatrolRoute,
    spawn: Vec3,
    opts: GuardCorpsOptions,
    /**
     * Which guard this is, used only to stagger the idle animations. Three
     * guards whose head-sweeps start at the same instant look like one guard
     * rendered three times, and the effect is instantly, uncannily wrong.
     */
    index = 0,
  ) {
    this.opts = opts;
    this.scanPhase = index * 0.37;
    this.callsign = route.name.toUpperCase();
    this.brain = new GuardBrain(id, route.at);
    this.figure = new GuardFigure(id);
    opts.stage.add(this.figure.root);

    this.pos.set(spawn.x, spawn.y + 0.05, spawn.z);
    this.lastX = this.pos.x;
    this.lastZ = this.pos.z;

    const world = opts.physics.world;
    this.body = world.createRigidBody(
      RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(
        this.pos.x,
        this.pos.y + HEIGHT / 2,
        this.pos.z,
      ),
    );
    this.collider = world.createCollider(
      RAPIER.ColliderDesc.capsule(Math.max(0.05, HEIGHT / 2 - M.radius), M.radius),
      this.body,
    );
    // Registered so no sight line — the guard's own or anyone else's — is
    // stopped by a person-shaped capsule. Guards still collide physically.
    opts.physics.registerActor(this.collider);

    const c = world.createCharacterController(SKIN);
    c.enableAutostep(M.stepHeight, M.radius * 0.75, false);
    c.enableSnapToGround(0.5);
    c.setMaxSlopeClimbAngle((52 * Math.PI) / 180);
    c.setMinSlopeSlideAngle((44 * Math.PI) / 180);
    c.setApplyImpulsesToDynamicBodies(false);
    c.setSlideEnabled(true);
    this.controller = c;

    // The torch's actual illumination. The visible shaft is geometry on the
    // figure; this is the pool it throws on the wall, and it is a candidate for
    // the four-light budget like everything else — a torch two rooms away is
    // correctly outranked by the shopfront you are standing in front of.
    this.torch = {
      position: new Vector3(),
      color: new Color(LIGHTS.torch.color),
      intensity: LIGHTS.torch.intensity,
      distance: LIGHTS.torch.distance,
      decay: LIGHTS.torch.decay,
    };
    this.dropTorch = opts.lights.add(this.torch);
  }

  get hasPath(): boolean {
    return this.leg < this.path.length;
  }

  /** One fixed tick: look, think, walk. Returns a line of radio traffic, if any. */
  step(dt: number, thieves: ThiefSense[], contacts: Contact[], mayRepath: boolean): string | null {
    this.grabbing = null;
    this.look(thieves);

    const atGoal = this.lastGoal
      ? Math.hypot(this.pos.x - this.lastGoal.x, this.pos.z - this.lastGoal.z) < ARRIVED_M &&
        Math.abs(this.pos.y - this.lastGoal.y) < 1.4
      : true;

    const intent = this.brain.think(dt, {
      pos: { x: this.pos.x, y: this.pos.y, z: this.pos.z },
      sightings: this.sightings,
      contacts,
      atGoal,
      unreachable: this.unreachable,
      zoneName: (at) => zoneLabel(this.opts.zones, at),
    });
    this.unreachable = false;
    if (intent.goal) this.lastGoal = { ...intent.goal };
    if (intent.grab) this.grabbing = intent.grab;

    if (intent.mode === 'downed') {
      this.downBlend = Math.min(1, this.downBlend + dt * 3.5);
      this.speed = 0;
      this.vel.set(0, this.vel.y, 0);
      this.fall(dt);
      this.figure.torchOn(false);
      return intent.say;
    }
    this.downBlend = Math.max(0, this.downBlend - dt * 2.2);
    this.figure.torchOn(true);

    this.steer(dt, intent.goal, mayRepath);
    this.walk(dt, intent.speed);
    this.aim(dt, intent);

    return intent.say;
  }

  /**
   * Adopt a transform that came off the wire.
   *
   * Speed is measured rather than sent, because the walk cycle needs the speed
   * the body is actually travelling on THIS machine after interpolation, not the
   * speed the host observed a hundred milliseconds ago — and a gait driven by a
   * stale number moonwalks. The capsule is moved with the figure so that a client
   * still cannot walk through a guard: local collision against a remote body is
   * exactly the case host authority is designed to be forgiving about, and being
   * physically stopped by the man with the torch has to happen on the frame you
   * see it, not on the frame the host says so.
   */
  applyNet(x: number, y: number, z: number, yaw: number, alert: number, mode: GuardMode): void {
    const dx = x - this.pos.x;
    const dz = z - this.pos.z;
    this.netSpeed = Math.hypot(dx, dz) / Math.max(1e-3, this.sinceNet);
    this.sinceNet = 0;
    this.pos.set(x, y, z);
    this.yaw = yaw;
    this.brain.adoptNet(mode, alert);
    const centre = { x, y: y + HEIGHT / 2, z };
    this.body.setNextKinematicTranslation(centre);
    this.body.setTranslation(centre, true);
  }

  private netSpeed = 0;
  private sinceNet = 1;

  /** Visual only: pose the figure and move the torch light. */
  render(dt: number): void {
    this.sinceNet += dt;
    // Only a networked guard has an externally-supplied speed; a locally
    // simulated one measures its own in `walk`.
    if (this.sinceNet < 1) this.speed += (this.netSpeed - this.speed) * Math.min(1, dt * 8);
    this.figure.root.position.copy(this.pos);
    this.figure.root.rotation.y = this.yaw;
    this.figure.pose({
      speed: this.speed,
      dt,
      headYaw: this.headYaw,
      headPitch: this.headPitch,
      down: this.downBlend,
    });
    this.figure.root.updateMatrixWorld(true);

    // The light sits a couple of metres down the beam rather than at the lamp:
    // a point light inside the guard's fist lights their own chest and the floor
    // they are standing on, which is the opposite of what a torch does.
    const from = this.figure.lampWorld(LAMP);
    const dir = this.figure.lampDir(DIR);
    this.torch.position.copy(from).addScaledVector(dir, 2.4);
    this.torch.intensity = this.downBlend > 0.5 ? 0 : LIGHTS.torch.intensity;
  }

  get snapshot() {
    return {
      id: this.id,
      mode: this.brain.mode as GuardMode,
      pos: [this.pos.x, this.pos.y, this.pos.z].map((v) => Number(v.toFixed(2))),
      yaw: Number(this.yaw.toFixed(2)),
      speed: Number(this.speed.toFixed(2)),
      target: this.brain.target,
      alert: Number(Math.max(0, ...this.brain.alert.values(), 0).toFixed(2)),
      path: this.path.length - this.leg,
      goal: this.pathGoal
        ? [this.pathGoal.x, this.pathGoal.y, this.pathGoal.z].map((v) => Number(v.toFixed(1)))
        : null,
    };
  }

  /** Back to the top of the beat with no memory of the last round. */
  reset(spawn: Vec3, startIdx = 0, dir: 1 | -1 = 1): void {
    this.brain.reset(startIdx, dir);
    this.pos.set(spawn.x, spawn.y + 0.05, spawn.z);
    this.vel.set(0, 0, 0);
    this.path.length = 0;
    this.leg = 0;
    this.pathGoal = null;
    this.lastGoal = null;
    this.repathIn = 0;
    this.stuckFor = 0;
    this.unreachable = false;
    this.wantsRepath = true;
    this.grabbing = null;
    this.downBlend = 0;
    this.speed = 0;
    this.lastX = this.pos.x;
    this.lastZ = this.pos.z;
    const centre = { x: this.pos.x, y: this.pos.y + HEIGHT / 2, z: this.pos.z };
    this.body.setNextKinematicTranslation(centre);
    this.body.setTranslation(centre, true);
  }

  dispose(): void {
    this.dropTorch();
    this.figure.root.removeFromParent();
    this.opts.physics.world.removeCollider(this.collider, false);
    this.opts.physics.world.removeRigidBody(this.body);
  }

  // -------------------------------------------------------------------------

  /**
   * Fill the sighting list.
   *
   * Cheap tests first and the raycast last, because the ray is the only part
   * that costs anything: a thief out of range or behind the guard never reaches
   * the broad phase at all.
   */
  private look(thieves: ThiefSense[]): void {
    this.sightings.length = 0;
    this.eye.x = this.pos.x;
    this.eye.y = this.pos.y + EYE;
    this.eye.z = this.pos.z;

    const gaze = this.yaw + this.headYaw;
    const fx = -Math.sin(gaze);
    const fz = -Math.cos(gaze);
    const cosView = Math.cos((G.viewDeg * Math.PI) / 360);
    const cosTorch = Math.cos((G.torchDeg * Math.PI) / 360);

    for (const t of thieves) {
      if (!t.active) continue;
      // Aim at the chest, not the feet: a thief the far side of a low counter is
      // hidden from the knees down and perfectly visible from the ribs up, and
      // the ray has to agree with what the player can see of themselves.
      const chest = (t.crouching ? M.crouchHeight : M.standHeight) * 0.72;
      const hx = t.pos.x - this.eye.x;
      const hy = t.pos.y + chest - this.eye.y;
      const hz = t.pos.z - this.eye.z;
      const dist = Math.sqrt(hx * hx + hy * hy + hz * hz);
      if (dist > G.viewRangeM) continue;

      const inv = dist > 1e-4 ? 1 / dist : 0;
      const dot = (hx * fx + hz * fz) * inv;
      const inCone = dot >= cosView || dist < G.pointBlankM;
      let visible = false;
      let lit = false;
      if (inCone) {
        const toi = this.opts.physics.ray(
          this.eye,
          { x: hx * inv, y: hy * inv, z: hz * inv },
          dist - 0.15,
          this.collider,
        );
        visible = toi === null;
        lit = visible && dot >= cosTorch && dist <= G.torchRangeM;
      }
      this.sightings.push({
        id: t.id,
        pos: { x: t.pos.x, y: t.pos.y, z: t.pos.z },
        dist,
        visible,
        lit,
        crouching: t.crouching,
        speed: t.speed,
        exposure: t.exposure,
      });
    }
  }

  /**
   * Keep a usable path to `goal`, and consume it.
   *
   * The reasons a path goes stale are all here in one place: there isn't one,
   * the goal has moved, it has been a while, or the guard is walking into
   * something. Everything else just walks the corners it already has.
   */
  private steer(dt: number, goal: Vec3 | null, mayRepath: boolean): void {
    this.repathIn -= dt;
    if (!goal) {
      this.path.length = 0;
      this.leg = 0;
      this.pathGoal = null;
      this.wantsRepath = false;
      return;
    }

    const drifted =
      !this.pathGoal ||
      Math.hypot(goal.x - this.pathGoal.x, goal.z - this.pathGoal.z) > GOAL_DRIFT_M ||
      Math.abs(goal.y - this.pathGoal.y) > 1.0;
    this.wantsRepath = drifted || !this.hasPath || this.repathIn <= 0 || this.stuckFor > STUCK_SEC;

    if (this.wantsRepath && mayRepath) {
      const found = this.opts.nav.path(this.pos, goal, this.path);
      this.leg = 0;
      this.pathGoal = { ...goal };
      this.repathIn = REPATH_SEC;
      this.stuckFor = 0;
      this.wantsRepath = false;
      // The grid answers "different island" by leaving the route empty. That is
      // the one failure the brain has to hear about: it means no amount of
      // walking gets there, and the goal itself has to change.
      if (!found && this.path.length === 0) this.unreachable = true;
    }

    // Skip corners already reached. More than one per tick, because the smoother
    // leaves short legs around a doorway and a guard should not stutter at each.
    while (this.leg < this.path.length) {
      const w = this.path[this.leg];
      if (Math.hypot(this.pos.x - w.x, this.pos.z - w.z) > WAYPOINT_M || Math.abs(this.pos.y - w.y) > 1.2) break;
      this.leg++;
    }
  }

  /** Accelerate toward the next corner, collide, slide, and fall. */
  private walk(dt: number, want: number): void {
    const w = this.leg < this.path.length ? this.path[this.leg] : null;
    let tx = 0;
    let tz = 0;
    if (w && want > 0) {
      const dx = w.x - this.pos.x;
      const dz = w.z - this.pos.z;
      const len = Math.hypot(dx, dz);
      if (len > 1e-3) {
        // Ease into the last corner so a guard arriving at a patrol node does
        // not skid to a halt on the spot.
        const isLast = this.leg === this.path.length - 1;
        const trim = isLast ? Math.min(1, len / 1.2) : 1;
        tx = (dx / len) * want * trim;
        tz = (dz / len) * want * trim;
      }
    }

    const accel = 11 * dt;
    this.vel.x = approach(this.vel.x, tx, Math.abs(tx - this.vel.x) * accel + accel * 2);
    this.vel.z = approach(this.vel.z, tz, Math.abs(tz - this.vel.z) * accel + accel * 2);
    this.fall(dt);

    // Stuck detection runs on ground actually covered, not on requested speed:
    // a guard pressed against a doorframe is asking to move at full pace.
    const moved = Math.hypot(this.pos.x - this.lastX, this.pos.z - this.lastZ);
    this.lastX = this.pos.x;
    this.lastZ = this.pos.z;
    this.speed = moved / Math.max(1e-4, dt);
    if (want > 0.2 && this.speed < STUCK_MPS) this.stuckFor += dt;
    else this.stuckFor = 0;
  }

  /** Gravity and one collide-and-slide step. Shared by walking and lying down. */
  private fall(dt: number): void {
    this.vel.y += M.gravity * dt;
    if (this.vel.y < -32) this.vel.y = -32;

    STEP_V.copy(this.vel).multiplyScalar(dt);
    this.controller.computeColliderMovement(this.collider, STEP_V);
    const moved = this.controller.computedMovement();
    this.pos.x += moved.x;
    this.pos.y += moved.y;
    this.pos.z += moved.z;
    if (this.controller.computedGrounded() && this.vel.y < 0) this.vel.y = 0;

    const centre = { x: this.pos.x, y: this.pos.y + HEIGHT / 2, z: this.pos.z };
    this.body.setNextKinematicTranslation(centre);
    this.body.setTranslation(centre, true);
  }

  /**
   * Where the guard is facing.
   *
   * The body turns toward whatever it is dealing with; the head turns faster and
   * further, and is what the vision cone and the torch actually use. That split
   * is what makes a guard look like it is checking a room rather than pivoting
   * like a turret — and it means a thief can watch the beam swing off them a
   * moment before the body follows, which is the cue to move.
   */
  private aim(dt: number, intent: { look: Vec3 | null; scan: boolean; mode: GuardMode }): void {
    let want = this.yaw;
    if (intent.look) {
      want = Math.atan2(-(intent.look.x - this.pos.x), -(intent.look.z - this.pos.z));
    } else if (Math.hypot(this.vel.x, this.vel.z) > 0.4) {
      want = Math.atan2(-this.vel.x, -this.vel.z);
    }

    if (intent.scan) {
      // Standing and looking around. Deterministic sweep, and slow — a fast one
      // reads as a malfunctioning turret rather than somebody being thorough.
      this.scanPhase += dt * SCAN_RATE;
      want += Math.sin(this.scanPhase * Math.PI * 2) * SCAN_ARC;
    }

    const delta = wrapPi(want - this.yaw);
    const rate = (intent.mode === 'chase' || intent.mode === 'alerted' ? TURN_FAST : TURN_SLOW) * dt;
    this.yaw += Math.abs(delta) <= rate ? delta : Math.sign(delta) * rate;
    this.yaw = wrapPi(this.yaw);

    // Whatever the body has not caught up with yet, the head is already doing.
    const lead = wrapPi(want - this.yaw);
    this.headYaw = approach(this.headYaw, clamp(lead, -NECK, NECK), dt * 7);

    const look = intent.look;
    const wantPitch = look
      ? clamp(
          Math.atan2(look.y + 1.4 - (this.pos.y + EYE), Math.hypot(look.x - this.pos.x, look.z - this.pos.z)),
          -0.5,
          0.4,
        )
      : 0;
    this.headPitch = approach(this.headPitch, wantPitch, dt * 4);
  }
}

// ---------------------------------------------------------------------------

const STEP_V = new Vector3();
const LAMP = new Vector3();
const DIR = new Vector3();

/**
 * What a guard calls the place it is standing, on the radio.
 *
 * Zone ids are for the builder; these are for a person speaking out loud. The
 * table is deliberately coarse — "level two, west" is useful over a walkie-talkie
 * and "unit e-l1-3" is not — and anything unmapped falls back to a bearing,
 * which is still better than silence.
 */
const ZONE_NAMES: Record<string, string> = {
  atrium: 'the atrium',
  canopy: 'the atrium',
  'shops-w-l0': 'the west shopfronts',
  'shops-e-l0': 'the east shopfronts',
  'level1-w': 'level two, west',
  'level1-e': 'level two, east',
  bridge: 'the bridge',
  'svc-w-l0': 'the west service corridor',
  'svc-e-l0': 'the east service corridor',
  'svc-w-l1': 'service, level two west',
  'svc-e-l1': 'service, level two east',
  foodcourt: 'the food court',
  'fc-balcony': 'the food court balcony',
  control: 'the control room',
  dock: 'the loading dock',
  plaza: 'the plaza',
  'plaza-canopy': 'the plaza',
};

export function zoneLabel(zones: ZoneDef[], at: Vec3): string {
  const zone = zoneAt(zones, at.x, at.y, at.z);
  const named = zone && ZONE_NAMES[zone.id];
  if (named) return named;
  // No zone: outside the building, or a point the search ring threw into a wall.
  const ns = at.z < -20 ? 'north' : at.z > 12 ? 'south' : 'mid';
  const ew = at.x < -6 ? 'west' : at.x > 6 ? 'east' : 'centre';
  return `${ns} ${ew}`;
}

function approach(v: number, target: number, maxStep: number): number {
  const d = target - v;
  if (Math.abs(d) <= maxStep) return target;
  return v + Math.sign(d) * maxStep;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Shortest signed angle, so turning from +179° to -179° is two degrees. */
function wrapPi(a: number): number {
  const t = (a + Math.PI) % (Math.PI * 2);
  return (t < 0 ? t + Math.PI * 2 : t) - Math.PI;
}
